// REALISTIC SEA CREATURES (2026-10-23, owner directive "really realistic
// models"): the ambient SHARK and the pirate-ship KRAKEN boss are licensed
// glTF assets (Babylon.js Assets, CC-BY 4.0 — see assetCredits.ts and
// public/models/{shark,kraken}/CREDITS.md) instead of procedural primitives.
//
// Gameplay is untouched: the callers keep their own transforms, hit tests and
// movement; these builders only produce a visual that fills the SAME extent as
// the procedural model it replaces (sizes below were measured off the
// procedural builders). Each builder returns null when the asset is not usable
// — weak machines (lowSpec / TV) never even fetch it, and a failed load keeps
// the procedural model forever (gltfAssets resolves null, never throws).
//
// Shared resources: the cached glTF's geometry / material / textures are
// tagged userData.shared by gltfAssets; every dispose walker in ambientLife.ts
// and monsters.ts skips them. A per-instance Skeleton (bone texture) IS
// per-instance and is freed by disposeSkinned().
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfIfReady, instantiate, loadGltf, realModelsEnabled } from "./gltfAssets.js";

export const SHARK_URL = "/models/shark/shark.glb";
export const KRAKEN_URL = "/models/kraken/kraken.glb";

/** Real models on every machine except the TV app (owner rule 2026-11-04,
 *  see gltfAssets realModelsEnabled). Off outside a browser (the node benches
 *  census the procedural build). */
export function seaModelsEnabled(): boolean {
  return realModelsEnabled();
}

/** Start the fetch early (map load). No-op when disabled. */
export function preloadSeaModel(url: string): Promise<GLTF | null> {
  if (!seaModelsEnabled()) return Promise.resolve(null);
  void gltfIfReady(url); // arms the synchronous ready-peek before anyone awaits
  return loadGltf(url);
}

/** The asset if it is already parsed and allowed on this machine. */
export function seaModelIfReady(url: string): GLTF | null {
  return seaModelsEnabled() ? gltfIfReady(url) : null;
}

// hoisted scratch — the per-frame kraken pose allocates nothing
const Q_A = new THREE.Quaternion();
const Q_B = new THREE.Quaternion();
const V_A = new THREE.Vector3();
const V_B = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);

/** Free what a skinned clone owns (its Skeleton's bone texture) — the
 *  geometry/material are the cached asset's and stay alive. */
export function disposeSkinned(root: THREE.Object3D): void {
  root.traverse((o) => {
    const sm = o as THREE.SkinnedMesh;
    if (sm.isSkinnedMesh) sm.skeleton.dispose();
  });
}

// ---------------------------------------------------------------- SHARK

/** Procedural shark length (snout tip → caudal tip, at group scale 1). The
 *  ambient spawner scales the group 1.5–2.4x on top, so a 7.5–12 m animal. */
const SHARK_LEN = 5.0;

interface SharkAsset { k: number; cx: number; cy: number; cz: number; swim: THREE.AnimationClip; bite: THREE.AnimationClip; circle: THREE.AnimationClip; }
const sharkAssets = new WeakMap<GLTF, SharkAsset>();

/** Strip the ROOT bone's motion from a clip: the swimmer is moved and
 *  steered by code (updateSwimmers), so a clip that also drifts — or, like
 *  `circling`, turns the root a full 360° over its 32 s — would fight it. The
 *  body's sinuous bend lives on the spine bones and stays. */
function inPlace(clip: THREE.AnimationClip, rootName: string, keepTurn: boolean): THREE.AnimationClip {
  const c = clip.clone();
  c.tracks = c.tracks.filter((t) => t.name !== `${rootName}.position` && (keepTurn || t.name !== `${rootName}.quaternion`));
  return c;
}

function sharkAsset(g: GLTF): SharkAsset | null {
  let a = sharkAssets.get(g);
  if (a) return a;
  const find = (n: string): THREE.AnimationClip | undefined => g.animations.find((c) => c.name === n);
  const swim = find("swimming"), bite = find("bite"), circle = find("circling");
  if (!swim || !bite || !circle) return null;
  g.scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(g.scene, true);
  if (box.isEmpty()) return null;
  let root = "", spineY = (box.min.y + box.max.y) / 2;
  g.scene.traverse((o) => {
    if (!(o as THREE.Bone).isBone) return;
    if (!root && /root/i.test(o.name)) root = o.name;
    if (/^Spine1/.test(o.name)) spineY = o.getWorldPosition(V_A).y;
  });
  const len = box.max.z - box.min.z;
  a = {
    k: SHARK_LEN / len,
    cx: (box.min.x + box.max.x) / 2, cy: spineY, cz: (box.min.z + box.max.z) / 2,
    swim: inPlace(swim, root, true), bite: inPlace(bite, root, true), circle: inPlace(circle, root, false),
  };
  sharkAssets.set(g, a);
  return a;
}

export interface SharkRig {
  /** Add this to the swimmer's group: faces -z, body axis on y=0, centred. */
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  swim: THREE.AnimationAction;
  bite: THREE.AnimationAction;
  circle: THREE.AnimationAction;
  /** seconds until the next bite / circling flourish */
  nextMove: number;
  /** >= 0 while a flourish plays: seconds until the swim cycle fades back */
  backIn: number;
}

/** A realistic shark clone, or null when the asset is unavailable. */
export function buildSharkModel(g: GLTF): SharkRig | null {
  const a = sharkAsset(g);
  if (!a) return null;
  const inst = instantiate(g);
  inst.position.set(-a.cx, -a.cy, -a.cz);
  const root = new THREE.Group();
  root.add(inst);
  root.scale.setScalar(a.k);
  root.rotation.y = Math.PI; // the asset swims toward +z; every swimmer here faces -z
  root.updateMatrixWorld(true);
  inst.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; } // under the sea surface, like the procedural one
  });
  const mixer = new THREE.AnimationMixer(inst);
  const swim = mixer.clipAction(a.swim);
  const bite = mixer.clipAction(a.bite);
  const circle = mixer.clipAction(a.circle);
  bite.setLoop(THREE.LoopOnce, 1);
  bite.clampWhenFinished = false;
  swim.play();
  swim.time = Math.random() * a.swim.duration; // the school must not beat in phase
  return { root, mixer, swim, bite, circle, nextMove: 4 + Math.random() * 8, backIn: -1 };
}

/** Per-frame shark animation: the swim cycle at a rate that tracks the
 *  swimmer's speed, with an occasional bite snap or a stretch of the circling
 *  clip's lazy sway. Cross-fades only on those rare events; the steady state
 *  is one mixer.update (no allocation). */
export function updateSharkModel(r: SharkRig, dt: number, speed: number): void {
  r.swim.timeScale = 0.55 + speed * 0.22;
  if (r.backIn >= 0) {
    r.backIn -= dt;
    if (r.backIn < 0) {
      r.swim.reset().fadeIn(0.35).play();
      if (r.circle.isRunning()) r.circle.fadeOut(0.35);
      if (r.bite.isRunning()) r.bite.fadeOut(0.35);
      r.nextMove = 7 + Math.random() * 9;
    }
  } else if ((r.nextMove -= dt) <= 0) {
    r.swim.fadeOut(0.2);
    if (Math.random() < 0.6) {
      r.bite.reset().fadeIn(0.2).play(); // one snap of the jaws
      r.backIn = r.bite.getClip().duration - 0.25;
    } else {
      const c = r.circle.getClip().duration;
      r.circle.reset().fadeIn(0.6).play();
      r.circle.time = Math.random() * Math.max(0, c - 6);
      r.backIn = 4 + Math.random() * 2;
    }
  }
  r.mixer.update(dt);
}

// ---------------------------------------------------------------- KRAKEN
//
// The octopus rig has 8 arms of 10 bones each (`Bone<arm><seg>`, every bone's
// chain axis is its local +Z) and only eye clips — the arms are posed in code:
// a travelling sine wave runs down every chain, the front arms rear up on the
// windup (mode 1), whip down-and-out with the tips curling on the SLAM
// (mode 2), and everything goes limp when slain (mode 9). Same modes, same
// timings as the procedural squid; the HP / hit box live server-side and are
// unchanged (krakenAabb: ±5.5 m around the body, y-1..y+9).

/** Mantle width to match: the procedural mantle is ~10.8 m wide and the
 *  server hit box 11 m, so the visible head fills the box. */
const KRAKEN_HEAD_W = 10;
/** Arm-root height in MonsterMesh-local metres (procedural arm bases: 1.6;
 *  the body sits at y -2.2 on pirate_ship, so the arms leave the head just
 *  under the waterline). */
const KRAKEN_ROOT_Y = 2.4;
/** The reared mantle stands BEHIND the eyes; nudge the animal forward so the
 *  mantle — the big thing players shoot at — is centred on the hit box. */
const KRAKEN_ROOT_Z = -1.8;
/** Per-bone uniform scale down each arm: real octopus arms are ~3x the
 *  mantle, which at kraken size would be 35 m whips. 0.92 compounding keeps
 *  ~71% of the length with a stronger taper (procedural reach was ~14 m). */
const ARM_SHRINK = 0.92;
/** Pose tables below are authored for a 10-bone arm; other rigs resample them
 *  by position along the arm and scale the per-bone bend by 9/(n-1), so the
 *  whole arm takes the same curve whatever its bone count. */
const REF_BONES = 10;
/** Arm bone name → [arm, segment]. Two rigs are supported: the Babylon octopus
 *  (`Bone<a><s>`, 10 per arm) and Kraken v2 (`Arm<a>_<s>`, 5 per arm, renamed
 *  by agent/kraken-asset/pipeline.mjs). */
function armBone(name: string): [number, number] | null {
  const m = /^Bone(\d)(\d)$/.exec(name) ?? /^Arm(\d)_(\d)$/.exec(name);
  return m ? [Number(m[1]), Number(m[2])] : null;
}
const isMantleRoot = (name: string): boolean => name === "Head10" || name === "Mantle0";
/** The asset's mantle (`Head10`) lies back flat behind the eyes, like a
 *  resting octopus on the sea floor. Reared up by this much (rad) it stands
 *  out of the water behind the eyes the way the squid's mantle did. */
const MANTLE_UP = 0.75;
/** Kraken v2's mantle already stands up at bind pose. */
const KRAKEN2_MANTLE_UP = 0;
/** Kraken v2 per-bone arm scale (5-bone arms; see buildKrakenModel). */
const KRAKEN2_ARM_SHRINK = 0.66;

interface ArmAxes { lift: THREE.Vector3[]; side: THREE.Vector3[]; front: number; ph: number; /** extra root lift (rad) that brings a hanging bind-pose arm level, so the pose tables start from the same place on every rig */ fix: number }
interface KrakenAsset {
  k: number; armAxes: Map<number, ArmAxes>; mantleLift: THREE.Vector3 | null; eyeClips: THREE.AnimationClip[]; radius: number;
  /** bones per arm, and the per-bone bend scale vs the 10-bone tables */
  n: number; bend: number;
  /** extra mantle rear (rad): the Babylon mantle lies flat, Kraken v2's stands */
  mantleUp: number;
  /** +1: the asset faces +z (Babylon octopus); -1: it faces -z (Kraken v2) */
  faceZ: number;
}
const krakenAssets = new WeakMap<GLTF, KrakenAsset>();

function krakenAsset(g: GLTF): KrakenAsset | null {
  const hit = krakenAssets.get(g);
  if (hit) return hit;
  g.scene.updateMatrixWorld(true);
  let mesh: THREE.SkinnedMesh | null = null;
  g.scene.traverse((o) => { if (!mesh && (o as THREE.SkinnedMesh).isSkinnedMesh) mesh = o as THREE.SkinnedMesh; });
  if (!mesh) return null;
  const sm = mesh as THREE.SkinnedMesh;
  // HEAD width: the bind-pose spread of vertices whose dominant joint is a
  // head/eye bone (the arms are excluded — they are what we re-pose)
  const bones = sm.skeleton.bones;
  const headJ = new Set<number>();
  bones.forEach((b, i) => { if (/^(Head|eye|Mantle)/.test(b.name)) headJ.add(i); });
  const si = sm.geometry.getAttribute("skinIndex"), sw = sm.geometry.getAttribute("skinWeight");
  let minX = Infinity, maxX = -Infinity, rad = 0;
  for (let i = 0; i < si.count; i++) {
    sm.getVertexPosition(i, V_A); // SkinnedMesh: already bone-transformed, mesh-local
    rad = Math.max(rad, V_A.length());
    V_A.applyMatrix4(sm.matrixWorld); // → asset-scene units (what root.scale multiplies)
    let best = 0, bw = -1;
    for (let c = 0; c < 4; c++) { const w = sw.getComponent(i, c); if (w > bw) { bw = w; best = si.getComponent(i, c); } }
    if (!headJ.has(best)) continue;
    if (V_A.x < minX) minX = V_A.x;
    if (V_A.x > maxX) maxX = V_A.x;
  }
  if (!(maxX > minX)) return null;
  // per-arm bend axes, in each bone's LOCAL frame at bind pose: `lift` turns
  // the bone's +Z chain axis toward world up, `side` swings it sideways
  const armAxes = new Map<number, ArmAxes>();
  let mantleLift: THREE.Vector3 | null = null;
  const liftAxis = (b: THREE.Bone): THREE.Vector3 => {
    b.getWorldQuaternion(Q_A).invert();
    return new THREE.Vector3().crossVectors(Z_AXIS, V_B.copy(UP).applyQuaternion(Q_A)).normalize();
  };
  const babylon = bones.some((b) => b.name === "Head10");
  // which way the animal looks: the Babylon octopus faces +z, Kraken v2 −z
  const faceZ = babylon ? 1 : -1;
  // bones per arm (all arms must agree)
  let n = 0;
  for (const b of bones) { const ab = armBone(b.name); if (ab) n = Math.max(n, ab[1] + 1); }
  if (n < 4) return null;
  /** The chain axis in a bone's LOCAL frame: toward its child bone (the
   *  Babylon rig is +Z by construction; Kraken v2's is whatever Maya left). */
  const chainAxis = (b: THREE.Bone): THREE.Vector3 => {
    const child = b.children.find((c) => (c as THREE.Bone).isBone && c.position.lengthSq() > 1e-10);
    if (child) return child.position.clone().normalize();
    // the TIP has no child: rigs keep one bone-axis convention down a chain,
    // so it shares its parent's
    const parent = b.parent as THREE.Bone | null;
    return parent?.isBone && armBone(parent.name) ? chainAxis(parent) : Z_AXIS.clone();
  };
  for (const b of bones) {
    if (isMantleRoot(b.name)) mantleLift = liftAxis(b);
    const ab = armBone(b.name);
    if (!ab) continue;
    const [arm, seg] = ab;
    let ax = armAxes.get(arm);
    if (!ax) { ax = { lift: [], side: [], front: 0, ph: arm * 0.83, fix: 0 }; armAxes.set(arm, ax); }
    const along = chainAxis(b);
    if (seg === 0 && !babylon) {
      // world elevation of the root bone at bind pose: Kraken v2's arms HANG
      // (the Babylon octopus's lie level, which the pose tables assume)
      const dir = V_A.copy(along).applyQuaternion(b.getWorldQuaternion(Q_B));
      ax.fix = -Math.asin(Math.max(-1, Math.min(1, dir.y)));
    }
    b.getWorldQuaternion(Q_A).invert();
    const u = V_B.copy(UP).applyQuaternion(Q_A);
    ax.lift[seg] = new THREE.Vector3().crossVectors(along, u).normalize();
    ax.side[seg] = u.clone().addScaledVector(along, -u.dot(along)).normalize();
    if (seg === n - 1) {
      // the asset faces +z: an arm whose tip is at +z is a FRONT arm
      const p = b.getWorldPosition(V_A);
      ax.front = (faceZ * p.z / Math.max(1e-6, Math.hypot(p.x, p.z)) + 1) / 2;
    }
  }
  if (armAxes.size !== 8) return null;
  // a deeper, bloodier red-brown than the reef octopus the asset ships as —
  // this one drags ships under. Mutates the CACHED material once; the kraken
  // is the only user of this asset.
  const mat = sm.material as THREE.MeshStandardMaterial;
  // (Kraken v2 is recoloured offline by its pipeline — only the Babylon asset
  // needs the runtime cast)
  if (mat.color && bones.some((b) => b.name === "Head10")) mat.color.setRGB(0.86, 0.66, 0.68);
  for (const ax of armAxes.values()) if (ax.lift.length !== n || ax.lift.some((v) => !v || !Number.isFinite(v.x))) return null;
  const a: KrakenAsset = {
    n, bend: (REF_BONES - 1) / (n - 1),
    mantleUp: babylon ? MANTLE_UP : KRAKEN2_MANTLE_UP,
    faceZ,
    k: KRAKEN_HEAD_W / (maxX - minX),
    armAxes,
    mantleLift,
    eyeClips: g.animations.filter((c) => /^eye/i.test(c.name)),
    radius: rad,
  };
  krakenAssets.set(g, a);
  return a;
}

interface Arm { bones: THREE.Bone[]; rest: THREE.Quaternion[]; ax: ArmAxes }
export interface KrakenRig {
  /** Add to the MonsterMesh group: faces -z like every MonsterMesh. */
  root: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  eyes: THREE.AnimationAction[];
  nextLook: number;
  arms: Arm[];
  mantle: THREE.Bone | null; mantleRest: THREE.Quaternion; mantleLift: THREE.Vector3 | null;
  /** eased pose drivers (0..1) */
  rear: number; slam: number; limp: number;
  /** bones per arm, per-bone bend scale, extra mantle rear (see KrakenAsset) */
  n: number; bend: number; mantleUp: number;
}

export function buildKrakenModel(g: GLTF): KrakenRig | null {
  const a = krakenAsset(g);
  if (!a) return null;
  const inst = instantiate(g);
  const root = new THREE.Group();
  root.add(inst);
  root.scale.setScalar(a.k);
  root.rotation.y = a.faceZ > 0 ? Math.PI : 0; // monsters face -z
  root.position.set(0, KRAKEN_ROOT_Y, KRAKEN_ROOT_Z);
  const arms: Arm[] = [];
  const byArm = new Map<number, THREE.Bone[]>();
  let mantle: THREE.Bone | null = null;
  inst.traverse((o) => {
    if ((o as THREE.Bone).isBone && isMantleRoot(o.name)) mantle = o as THREE.Bone;
    const sm = o as THREE.SkinnedMesh;
    if (sm.isSkinnedMesh) {
      sm.castShadow = true;
      // arms re-pose far outside the bind pose: one sphere around the arm
      // roots that any pose stays inside (mesh-local units)
      sm.boundingSphere = new THREE.Sphere(new THREE.Vector3(), a.radius * 1.1);
    }
    const ab = (o as THREE.Bone).isBone ? armBone(o.name) : null;
    if (ab) {
      let list = byArm.get(ab[0]);
      if (!list) { list = []; byArm.set(ab[0], list); }
      list[ab[1]] = o as THREE.Bone;
    }
  });
  // same overall taper for any bone count; Kraken v2's arms are ~2x longer
  // relative to its mantle than the Babylon octopus's, so they taper harder
  // to land near the procedural squid's ~14 m reach
  const shrink = a.faceZ > 0 ? Math.pow(ARM_SHRINK, a.bend) : KRAKEN2_ARM_SHRINK;
  for (const [arm, bones] of byArm) {
    const ax = a.armAxes.get(arm);
    if (!ax || bones.length !== a.n) return null;
    bones.forEach((b, j) => { if (j > 0) b.scale.setScalar(shrink); });
    arms.push({ bones, rest: bones.map((b) => b.quaternion.clone()), ax });
  }
  const mixer = new THREE.AnimationMixer(inst);
  const eyes = a.eyeClips.map((c) => { const e = mixer.clipAction(c); e.setLoop(THREE.LoopOnce, 1); e.clampWhenFinished = false; return e; });
  const mb = mantle as THREE.Bone | null;
  return {
    root, mixer, eyes, nextLook: 1 + Math.random() * 3, arms,
    mantle: mb, mantleRest: mb ? mb.quaternion.clone() : new THREE.Quaternion(), mantleLift: a.mantleLift,
    rear: 0, slam: 0, limp: 0,
    n: a.n, bend: a.bend, mantleUp: a.mantleUp,
  };
}

/** Pose the kraken's arms for this frame. `t` is the mesh's running clock;
 *  `rear` (windup) / `slamK` (slam impact, 1 → 0) / `dying` are the same
 *  drivers the procedural squid used, derived from the replicated mode. */
export function updateKrakenModel(r: KrakenRig, t: number, dt: number, rear: number, slamK: number, dying: boolean): void {
  // eased drivers: the slam WHIPS in (fast) but never teleports a 20 m arm in
  // one frame; the death droop settles at the procedural rate (6.3/s)
  r.rear += (rear - r.rear) * (1 - Math.exp(-dt * 7));
  r.slam += (slamK - r.slam) * (1 - Math.exp(-dt * (slamK > r.slam ? 28 : 9)));
  r.limp += ((dying ? 1 : 0) - r.limp) * (1 - Math.exp(-dt * 6.3));
  const live = 1 - r.limp, sl = r.slam * live;
  for (const arm of r.arms) {
    const f = arm.ax.front;            // 1 = arm toward the target, 0 = trailing
    const raise = 0.3 + 0.7 * f;
    // the slam is thrown by the FRONT arms; the trailing ones only flinch
    const slA = sl * (0.35 + 0.65 * f), calmA = 1 - slA;
    const rearW = r.rear * (0.25 + 0.75 * f) * calmA * live;
    for (let jj = 0; jj < r.n; jj++) {
      const u = jj / (r.n - 1);
      // the 10-bone tables, indexed by the equivalent position along the arm
      const j = Math.round(u * (REF_BONES - 1));
      const k = jj === 0 ? 1 : r.bend;
      // REST: out under the surface from the root, then rising out of the
      // water in an S, the tip curling back
      const rest = j === 0 ? 0.5 : j <= 3 ? 0.3 : j <= 6 ? -0.11 : 0.22;
      // WINDUP: lift higher and arch back over the head, cobra-style
      const rearP = j <= 3 ? 0.14 : 0.2;
      // SLAM: whip down-and-out, slapping flat along the surface, the tips
      // coiling under as they strike
      const slamP = j === 0 ? 0.62 : j <= 3 ? 0.02 : j <= 6 ? -0.04 : -0.3;
      // DEAD: no tone at all — the arm just hangs off the sinking body
      const limpP = j === 0 ? -0.15 : 0.03;
      // travelling wave down the chain (amplitude grows toward the tip)
      const wave = (0.06 + 0.14 * u) * Math.sin(t * 1.4 - j * 0.75 + arm.ax.ph);
      const sway = (0.035 + 0.07 * u) * Math.sin(t * 0.9 - j * 0.5 + arm.ax.ph * 1.3);
      const lift = k * (live * (calmA * (raise * rest + wave) + rearW * rearP + slA * slamP) + r.limp * (limpP + wave * 0.1))
        + (jj === 0 ? arm.ax.fix : 0);
      const side = k * sway * (live * (1 - 0.6 * slA) + r.limp * 0.15);
      Q_A.setFromAxisAngle(arm.ax.lift[jj], lift);
      Q_B.setFromAxisAngle(arm.ax.side[jj], side);
      arm.bones[jj].quaternion.copy(arm.rest[jj]).multiply(Q_A).multiply(Q_B);
    }
  }
  // MANTLE: reared up behind the eyes, breathing; it swells back on the
  // windup, pitches forward into the slam, and slumps when slain
  if (r.mantle && r.mantleLift) {
    const up = r.mantleUp + Math.sin(t * 1.1) * 0.04 + r.rear * 0.12 * live - sl * 0.2 - r.limp * 0.55;
    r.mantle.quaternion.copy(r.mantleRest).multiply(Q_A.setFromAxisAngle(r.mantleLift, up));
  }
  // the eyes glance about now and then (the asset's own eye clips)
  if (!dying && r.eyes.length && (r.nextLook -= dt) <= 0) {
    r.nextLook = 3.2 + Math.random() * 4; // clips are 3 s: never two at once
    r.eyes[(Math.random() * r.eyes.length) | 0].reset().play();
  }
  r.mixer.update(dt);
}
